import { useQueryClient } from "@tanstack/react-query"
import LogRocket from "logrocket"
import React, { createContext, useContext, useState } from "react"
import { clearMagicLinkSignIn } from "../utils/magicLinkSession"

interface Organisation {
  id: number
  name: string
  companyNumber: string
  organisationType: string
  isOnboarded: boolean
  homeQuota: number
  totalHomes: number
}

interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  companyName?: string
  companyNumber?: string
  organisation?: Organisation
}

interface AuthContextType {
  token: string | null
  user: User | null
  login: (token: string) => void
  logout: () => void
  setUser: (user: User) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token")
  )
  const [user, setUserState] = useState<User | null>(null)
  const queryClient = useQueryClient()

  const setUser = (userData: User) => {
    setUserState(userData)
    // Identify user in LogRocket only in production
    if (userData.id && process.env.NODE_ENV === "production") {
      LogRocket.identify(userData.id.toString(), {
        email: userData.email || "",
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        companyName: userData.companyName || "",
        companyNumber: userData.companyNumber || ""
      })
    }
  }

  const login = (newToken: string) => {
    setToken(newToken)
    localStorage.setItem("token", newToken)
  }

  const logout = () => {
    setToken(null)
    setUserState(null)
    localStorage.removeItem("token")
    clearMagicLinkSignIn()
    queryClient.clear()
    // LogRocket will automatically start a new session on next page load
  }

  return (
    <AuthContext.Provider
      value={{ token, user, login, logout, setUser, isAuthenticated: !!token }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
